import { ThemeProvider } from '@emotion/react';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { PostHogProvider } from 'posthog-js/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { RecoilRoot } from 'recoil';
import './App.css';

import posthog from './analytics/posthog';
import { BadgeNotificationProvider } from './components/common/Notifications/BadgeNotificationProvider';
import { useValidateProtocol } from './hooks/useValidateProtocol';
import { AppRoutes } from './routes/routes';
import { theme } from './styles/theme';

// Inline FreeTrialExpiredModal to work around module resolution issue
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe } from './middleware/useMe';
import { isB2BUser } from './utils/user';

const InlineFreeTrialExpiredModal: React.FC = () => {
  const { user, trialRemainingDays } = useMe();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Check if user is individual free user (not B2B) with expired trial
  const shouldShowModal = React.useMemo(() => {
    return (
      user?.membershipTier === 'free' &&
      !isB2BUser(user) &&
      trialRemainingDays === 0
    );
  }, [user, trialRemainingDays]);

  useEffect(() => {
    if (!user || !shouldShowModal) return;

    const trialExpiresAt = user.trialExpiresAt || '';
    const key = `trial-expired-modal-shown-${user.id}-${trialExpiresAt}`;

    if (!localStorage.getItem(key)) {
      setOpen(true);
      localStorage.setItem(key, 'true');
    }
  }, [user, shouldShowModal]);

  const handleUpgrade = useCallback(() => {
    setOpen(false);
    navigate('/account#subscription');
  }, [navigate]);

  if (!shouldShowModal) return null;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
        Your free trial has ended, but don't stop practicing!
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 2 }}>
        <Typography sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
          Sign up to our Starter or Pro packages to keep levelling up!
        </Typography>
        <Typography sx={{ mb: 2, textAlign: 'center' }}>
          You will always be able to continue practicing if you just need to
          brush up by using our Quick Start option.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 5, pt: 3 }}>
        <Button
          onClick={handleUpgrade}
          color="primary"
          variant="contained"
          autoFocus
        >
          Upgrade Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchInterval: 1000 * 60, retry: 0 } },
});

function App() {
  useValidateProtocol({ host: 'practiceinterviews.com' });

  return (
    <RecoilRoot>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <InlineFreeTrialExpiredModal />
              <AppRoutes />
              <BadgeNotificationProvider />
            </LocalizationProvider>
          </ThemeProvider>
        </QueryClientProvider>
        <ToastContainer />
      </PostHogProvider>
    </RecoilRoot>
  );
}

export default App;
